<template>
  <div class="omx-taginput" :class="{ 'omx-taginput--view-only': viewOnly }">
    <label v-if="label" class="omx-taginput__label">{{ label }}</label>

    <div v-if="viewOnly" class="omx-taginput__tags">
      <template v-if="hasValues">
        <b-tag
            v-for="(tag, i) in normalizedValue"
            :key="tagKey(tag, i)"
            type="is-info is-light"
            size="is-medium"
        >
          <slot name="tag" :tag="tag">{{ tagLabel(tag) }}</slot>
        </b-tag>
      </template>
      <span v-else class="omx-taginput__empty">—</span>
    </div>

    <b-taginput
        v-else
        ref="taginput"
        :value="value"
        :data="data"
        :loading="loading"
        :maxtags="maxtags"
        :has-counter="false"
        :open-on-focus="true"
        :allow-new="allowNew"
        :autocomplete="autocompleteAttr"
        :field="field"
        :icon="icon"
        :remove-on-keys="removeOnKeys"
        :confirm-keys="effectiveConfirmKeys"
        :placeholder="placeholder"
        :tabindex="tabindex"
        @input="$emit('input', $event)"
        @typing="$emit('typing', $event)"
        @blur="$emit('blur', $event)"
        @add="$emit('add', $event)"
        @remove="$emit('remove', $event)"
    />
  </div>
</template>

<script>
/**
 * OmxTagInput — wraps b-taginput in the Omx visual language.
 *
 * View mode renders a b-taglist of the current value; edit mode renders
 * a b-taginput pre-wired with the conventions used across the app
 * (no counter, open on focus, no remove-on-keys, autocomplete).
 *
 * Use the `#tag` slot to customise how each tag is rendered in view mode
 * when the items are objects (e.g. user records). `displayField` is the
 * default extractor for primitive-display cases.
 *
 *   <OmxTagInput
 *       v-model="projectManagersTags"
 *       :label="$t('project-details-view.project-manager-field')"
 *       :data="filteredProjectManagers"
 *       :loading="isFetching"
 *       :view-only="!isEditMode"
 *       field="email"
 *       icon="account-box"
 *       @typing="getFilteredProjectManagers"
 *       @input="onProjectManagersInput"
 *   >
 *     <template #tag="{ tag }">{{ getFullName(tag.name) }}</template>
 *   </OmxTagInput>
 */
export default {
  name: "OmxTagInput",
  props: {
    label:        { type: String, default: null },
    value:        { type: Array, default: () => [] },
    data:         { type: Array, default: () => [] },
    loading:      { type: Boolean, default: false },
    maxtags:      { type: [Number, String], default: null },
    allowNew:     { type: Boolean, default: false },
    field:        { type: String, default: null },
    icon:         { type: String, default: null },
    placeholder:  { type: String, default: null },
    tabindex:     { type: [Number, String], default: null },
    removeOnKeys: { type: Array, default: () => [] },
    // When null, Buefy's default confirm keys (comma, tab) apply.
    confirmKeys:  { type: Array, default: null },
    viewOnly:     { type: Boolean, default: false },
    displayField: { type: String, default: null },
    autocompleteAttr: { type: [Boolean, String], default: true }
  },
  computed: {
    normalizedValue() {
      return Array.isArray(this.value) ? this.value : [];
    },
    hasValues() {
      return this.normalizedValue.some(v => v != null && v !== "");
    },
    effectiveConfirmKeys() {
      return this.confirmKeys == null ? undefined : this.confirmKeys;
    }
  },
  methods: {
    tagLabel(tag) {
      if (tag == null) return "";
      if (typeof tag === "string" || typeof tag === "number") return String(tag);
      const key = this.displayField || this.field;
      if (key && tag[key] != null) return String(tag[key]);
      return String(tag);
    },
    tagKey(tag, i) {
      if (tag && typeof tag === "object") {
        return tag.document_id || tag.id || tag.email || i;
      }
      return tag != null ? String(tag) + "_" + i : i;
    },
    focus() {
      const t = this.$refs.taginput;
      if (t && typeof t.focus === "function") t.focus();
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.omx-taginput {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
}
.omx-taginput__label {
  font-size: 12.5px;
  font-weight: 600;
  color: $omx-ink-700;
}
.omx-taginput__tags {
  min-height: 32px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}
.omx-taginput__empty {
  color: $omx-ink-400;
  font-size: 14px;
}
</style>